import { Icon, Tooltip } from '@dixa/lib-shared-habla';
import {
  formatInternational,
  getCountryCodeFromPhoneNumber,
  getPhoneNumberCountryCodeFormatted,
} from 'lib-shared-phone-numbers';
import _filter from 'lodash/filter';
import _orderBy from 'lodash/orderBy';
import { useCallback, useEffect, useRef, useState } from 'react';
import Collapsible from 'react-collapsible';
import { useSelector } from 'react-redux';

import {
  numbersApi,
  PhoneNumber,
  useLazyReadNumbersPaginatedQuery,
} from 'apis/numbers';
import { RootReducerStateT } from 'app/reducers/rootReducer';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { actions } from '../../../telephony/actions/telephony';

import ListNumbers from './ListNumbers';
import { PhoneNumbersCustomInput } from './PhoneNumbersCustomInput';

import '../styles/from-number-selector.less';

type PropsT = {
  fromNumberFormat: string | null;
  isOwnNumbersViewOpen: boolean;

  toggleOwnNumbersView: (isOpen: boolean) => void;
  setFromNumberCountryCode: (countryCode: string | null) => void;
  returnFromNumberFormat: (fromNumberFormat: string | null) => void;
};

export const OutboundNumberSelector = ({
  fromNumberFormat,
  isOwnNumbersViewOpen,
  toggleOwnNumbersView,
  setFromNumberCountryCode,
  returnFromNumberFormat,
}: PropsT): JSX.Element => {
  const dispatch = useAppDispatch();

  const selectedFromNumber = useSelector(
    (state: RootReducerStateT) => state.telephony.selectedFromNumber,
  );
  const agentDefaultNumber = useSelector(
    (state: RootReducerStateT) =>
      state.settings.profile.profile.default_outbound_number,
  );

  const [fromNumberRegion, setFromNumberRegion] = useState('Unknown');

  // Eagerly fetches the first page of numbers to populate the legacy Redux
  // slice (telephonyGetAllOwnNumbersSucceeded) and to drive hide/show logic
  // for the selector widget. The dynamic paginated query drives the browsable
  // list. Default-number resolution uses targeted queries below so we never
  // miss a default that lives beyond the first page.
  const {
    staticTelephoneNumbersArray,
    isStaticLoading,
    staticNumbersResponse,
  } = numbersApi.useReadNumbersQuery(
    { limit: 25 },
    {
      selectFromResult: ({ data, isLoading }) => ({
        staticTelephoneNumbersArray: data?.data?.numbers ?? [],
        isStaticLoading: isLoading,
        staticNumbersResponse: data,
      }),
    },
  );

  useEffect(() => {
    if (staticNumbersResponse) {
      dispatch(
        actions.telephonyGetAllOwnNumbersSucceeded(staticNumbersResponse),
      );
    }
  }, [staticNumbersResponse, dispatch]);

  // Fetches the org-level default outbound number. Result drives fallback
  // logic when the agent has not set a personal default.
  const { data: defaultNumberData } = numbersApi.useReadDefaultNumberQuery();
  const orgDefaultPhoneNumber = defaultNumberData?.data ?? null;

  // When the agent has a personal default, fetch it directly by search so we
  // always get the right PhoneNumber object even if it lives beyond page 1 of
  // the static list. Without this, the filter below only searches the first 25
  // numbers and falls back to the wrong number for large accounts.
  const { data: agentDefaultSearchData, isLoading: isAgentDefaultLoading } =
    numbersApi.useReadNumbersQuery(
      { searchTerm: agentDefaultNumber ?? undefined, limit: 1 },
      { skip: !agentDefaultNumber },
    );

  const agentDefaultPhoneNumber = agentDefaultNumber
    ? (agentDefaultSearchData?.data?.numbers?.find(
        (n) => n.e164_format === agentDefaultNumber,
      ) ?? null)
    : null;

  const {
    items: dynamicTelephoneNumbersArray,
    asyncStatus: dynamicAsyncStatus,
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
    changeArg,
  } = useLazyReadNumbersPaginatedQuery();

  useEffect(() => {
    // Always run on mount (asyncStatus is 'uninitialized') to populate the list
    // before the panel is opened. Re-run when the panel opens to refresh the list.
    // Intentionally skip when the panel closes to avoid an unnecessary re-fetch.
    if (dynamicAsyncStatus === 'uninitialized' || isOwnNumbersViewOpen) {
      changeArg({ limit: 15, searchTerm: undefined });
    }
    // dynamicAsyncStatus is intentionally excluded from deps — it is only used as
    // a one-time "has this ever fetched?" guard, not to react to every status change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwnNumbersViewOpen, changeArg]);

  useEffect(() => {
    if (dynamicAsyncStatus === 'fulfilled') {
      dispatch(
        actions.telephonyGetQueryNumbersSucceeded({
          data: { numbers: dynamicTelephoneNumbersArray },
          meta: {
            next_page: hasNextPage ? 'next' : null,
            prev_page: hasPreviousPage ? 'prev' : null,
          },
        }),
      );
    }
  }, [
    dynamicTelephoneNumbersArray,
    dynamicAsyncStatus,
    hasNextPage,
    hasPreviousPage,
    dispatch,
  ]);

  const orderedDynamicNumbers = _orderBy(dynamicTelephoneNumbersArray, [
    'e164_format',
  ]);

  const changeFromNumber = useCallback(
    (fromNumber: PhoneNumber | null = null) => {
      if (fromNumber === null) {
        return;
      }
      dispatch(actions.telephonyChangeFromNumber({ number: fromNumber }));
      const fromPhoneNumberFormat =
        formatInternational(fromNumber.e164_format) || fromNumber.e164_format;
      const region =
        getCountryCodeFromPhoneNumber(fromNumber.e164_format)?.toUpperCase() ||
        'Unknown';
      const countryCode =
        getPhoneNumberCountryCodeFormatted(fromNumber.e164_format) || null;
      setFromNumberCountryCode(countryCode);
      returnFromNumberFormat(fromPhoneNumberFormat);
      setFromNumberRegion(region);
    },
    [dispatch, setFromNumberCountryCode, returnFromNumberFormat],
  );

  const findNumberWithMatchingCountryCode = useCallback(
    (number: string | null = null): PhoneNumber | null => {
      // Use the resolved default as the pool for country-code matching.
      // Falls back to the static list only when no resolved default exists.
      let verifiedNumbers: readonly PhoneNumber[];
      if (agentDefaultPhoneNumber) {
        verifiedNumbers = [agentDefaultPhoneNumber];
      } else if (orgDefaultPhoneNumber) {
        verifiedNumbers = [orgDefaultPhoneNumber];
      } else {
        verifiedNumbers = _filter(staticTelephoneNumbersArray, {
          default: true,
        });

        if (verifiedNumbers.length === 0) {
          verifiedNumbers = [...staticTelephoneNumbersArray];
        }
      }

      if (verifiedNumbers.length === 0) {
        console.warn(
          '_findSameOwnNumberWithSameCountryCode no owned number given.',
        );
        return null;
      }

      let foundNumber: PhoneNumber | null = null;

      if (number !== null) {
        // find country code on number and match it with a from number (if any).
        const countryCode = getPhoneNumberCountryCodeFormatted(number);

        for (const ownedNumber of verifiedNumbers) {
          const ownedCountryCode = getPhoneNumberCountryCodeFormatted(
            ownedNumber.e164_format,
          );

          if (countryCode === ownedCountryCode) {
            foundNumber = ownedNumber;
            break;
          }
        }
      }
      return foundNumber;
    },
    [
      staticTelephoneNumbersArray,
      agentDefaultPhoneNumber,
      orgDefaultPhoneNumber,
    ],
  );

  const prevSelectedFromNumberRef = useRef(selectedFromNumber);

  useEffect(
    // Effect to resolve the initial "from number" when the component is rendered.
    function resolveInitialFromNumber() {
      // Wait for the agent's personal default to finish loading before resolving
      // the initial from-number — otherwise we'd fall back to the wrong number
      // while the targeted search request is still in flight.
      if (agentDefaultNumber && isAgentDefaultLoading) return;

      // Prefer the resolved default PhoneNumber objects (which are fetched
      // directly and are not limited by the 25-item static page). Fall back to
      // the first number with outgoing functionality from the static list.
      const resolvedDefault: PhoneNumber | null =
        agentDefaultPhoneNumber ??
        orgDefaultPhoneNumber ??
        _filter(staticTelephoneNumbersArray, {
          outgoing_functionality: true,
        })[0] ??
        null;

      if (selectedFromNumber === null && resolvedDefault !== null) {
        let outboundNumber = '';

        if (fromNumberFormat) {
          outboundNumber = fromNumberFormat;
        } else {
          outboundNumber = '+4560860931';
        }

        const fromNumber = findNumberWithMatchingCountryCode(outboundNumber);

        if (fromNumber !== null) {
          changeFromNumber(fromNumber);
        } else {
          changeFromNumber(resolvedDefault);
        }
      }

      if (prevSelectedFromNumberRef.current !== selectedFromNumber) {
        changeFromNumber(selectedFromNumber);
      }

      prevSelectedFromNumberRef.current = selectedFromNumber;
    },
    [
      staticTelephoneNumbersArray,
      selectedFromNumber,
      agentDefaultNumber,
      agentDefaultPhoneNumber,
      orgDefaultPhoneNumber,
      isAgentDefaultLoading,
      fromNumberFormat,
      findNumberWithMatchingCountryCode,
      changeFromNumber,
    ],
  );

  const onOwnNumberToggle = useCallback(() => {
    toggleOwnNumbersView(!isOwnNumbersViewOpen);
  }, [toggleOwnNumbersView, isOwnNumbersViewOpen]);

  const onSearch = useCallback(
    (searchTerm: string) => {
      changeArg(
        { searchTerm: searchTerm || undefined, limit: 15 },
        { debounceTime: 300 },
      );
    },
    [changeArg],
  );

  const staticLoaded = !isStaticLoading;

  let fromNumber: JSX.Element | null = null;
  let disabledPrevious = '';
  let disabledNext = '';
  let isHidden = '';
  let buttonsHidden = '';

  if (
    staticLoaded &&
    staticTelephoneNumbersArray.length > 0 &&
    dynamicAsyncStatus === 'fulfilled' &&
    orderedDynamicNumbers.length === 0
  ) {
    buttonsHidden = ' hidden';
  }

  if (selectedFromNumber && selectedFromNumber.name) {
    fromNumber = (
      <div className="call-from flags">
        <p className="m-b-5 noselect">Outbound number</p>
        <Tooltip content="Outbound call recording is active">
          <span
            className={
              'call-recording ' +
              (selectedFromNumber.record_outbound_calls === true
                ? ''
                : 'hidden')
            }
          >
            <Icon name="rec" />
          </span>
        </Tooltip>
        <span className={'flag ' + fromNumberRegion.toLowerCase()}></span>
        <Tooltip side="bottom" content={selectedFromNumber.name}>
          <span className="from-number-name">
            {fromNumberFormat} <em>({selectedFromNumber.name})</em>
          </span>
        </Tooltip>
        <Icon
          name="angle-right"
          size={20}
          className="Collapsible__trigger-icon"
        />
      </div>
    );
  } else if (selectedFromNumber) {
    fromNumber = (
      <div className="call-from flags">
        <p className="m-b-5 noselect">Outbound number</p>
        <Tooltip content="Outbound call recording is active">
          <span
            className={
              'call-recording ' +
              (selectedFromNumber.record_outbound_calls === true
                ? ''
                : 'hidden')
            }
          ></span>
        </Tooltip>
        <span className={'flag ' + fromNumberRegion.toLowerCase()}></span>
        <span>{fromNumberFormat}</span>
        <Icon
          name="angle-right"
          size={20}
          className="Collapsible__trigger-icon"
        />
      </div>
    );
  }

  if (!hasPreviousPage) {
    disabledPrevious = 'disabled';
  }

  if (!hasNextPage) {
    disabledNext = 'disabled';
  }

  if (staticLoaded && staticTelephoneNumbersArray.length === 0) {
    isHidden = ' hidden';
  }

  return (
    <div className={`collapsible-holder from-numbers-selector${isHidden}`}>
      <Collapsible
        handleTriggerClick={onOwnNumberToggle}
        open={isOwnNumbersViewOpen}
        trigger={fromNumber as any}
        transitionTime={300}
        easing="ease"
      >
        <li className="own-number-change dropdown-header" role="heading">
          Search outbound numbers
        </li>
        <PhoneNumbersCustomInput onSearch={onSearch} />
        <li
          className={`own-number-change${buttonsHidden} dropdown-header`}
          role="presentation"
        >
          Select outbound number
        </li>
        <ListNumbers
          dynamicTelephoneNumbersArray={orderedDynamicNumbers}
          onToggle={onOwnNumberToggle}
          selectedFromNumber={selectedFromNumber}
          changeFromNumber={changeFromNumber}
        />
        {dynamicAsyncStatus === 'pending' ? (
          <ul className={`pager${buttonsHidden}`} style={{ margin: 'auto' }}>
            <li className="disabled pull-left">
              <a type="button">
                {' '}
                <span style={{ verticalAlign: '-2px' }}>
                  <Icon name="arrow-left" />
                </span>
                Loading{' '}
              </a>
            </li>
            <li className="disabled pull-right">
              <a type="button">
                {' '}
                Loading{' '}
                <span style={{ verticalAlign: '-2px' }}>
                  {' '}
                  <Icon name="arrow-right" />{' '}
                </span>
              </a>
            </li>
          </ul>
        ) : (
          <ul className={`pager${buttonsHidden}`} style={{ margin: 'auto' }}>
            <li className={disabledPrevious + ' previous'}>
              {!hasPreviousPage ? (
                <a type="button">
                  <span style={{ verticalAlign: '-2px' }}>
                    <Icon name="arrow-left" />
                  </span>
                  Prev Page
                </a>
              ) : (
                <a
                  type="button"
                  style={{ cursor: 'pointer' }}
                  onClick={() => previousPage()}
                >
                  <span style={{ verticalAlign: '-2px' }}>
                    <Icon name="arrow-left" />
                  </span>
                  Prev Page
                </a>
              )}
            </li>
            <li className={disabledNext + ' next'}>
              {!hasNextPage ? (
                <a className={buttonsHidden} type="button">
                  Next page
                  <span style={{ verticalAlign: '-2px' }}>
                    <Icon name="arrow-right" />
                  </span>
                </a>
              ) : (
                <a
                  className={buttonsHidden}
                  type="button"
                  style={{ cursor: 'pointer' }}
                  onClick={() => nextPage()}
                >
                  Next page
                  <span style={{ verticalAlign: '-2px' }}>
                    <Icon name="arrow-right" />
                  </span>
                </a>
              )}
            </li>
          </ul>
        )}
      </Collapsible>
      <li
        className="divider"
        role="separator"
        style={{ margin: '13px 0 0 0', width: '100%', listStyleType: 'none' }}
      />
    </div>
  );
};
